import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceArea,
} from "recharts";
import apiService, { LivestreamTimelineResponse } from "../../services/api";
import {
  formatLivestreamTableViewsCount,
  formatNumber,
  formatExplorerTableDateLine,
  formatExplorerTableTimeLine,
  LIVESTREAM_DETAIL_GC_TIME,
  LIVESTREAM_DETAIL_STALE_TIME,
  programWindowAccent,
  programWindowChartAccents,
} from "./utils";
import type { TimelineProgramWindow } from "./LivestreamDetailRecentList";

const VIEWER_CHART_INTERVAL_SEC = 60;

function formatLivestreamChartDateTime(ms: number): string {
  const iso = new Date(ms).toISOString();
  return `${formatExplorerTableDateLine(iso)} ${formatExplorerTableTimeLine(iso)}`;
}

function formatLivestreamAxisTime(ms: number): string {
  return formatExplorerTableTimeLine(new Date(ms).toISOString());
}

type ReleaseChartPoint = {
  offsetSeconds: number;
  viewers: number;
  likes?: number;
  videoViewCount?: number;
};
type ReleaseChartPointWithAxis = ReleaseChartPoint & { axisTimeMs: number };
type LegacyChartPoint = {
  time: string;
  axisTimeMs?: number;
  viewers: number | undefined;
  likes: number | undefined;
  videoViewCount?: number;
};
type ChartDataPoint = ReleaseChartPointWithAxis | LegacyChartPoint;

function programWindowsCacheKey(windows: TimelineProgramWindow[]): string {
  return windows
    .map(
      (window) =>
        `${window.id}\t${window.startedAt}\t${window.endedAt ?? ""}\t${window.name}`,
    )
    .join("\n");
}

type PreparedViewerChart = {
  key: string;
  releaseMs: number;
  chartData: ChartDataPoint[];
  effectiveUseReleaseWindow: boolean;
  releaseChartDomainMax: number | null;
  legacyHasAbsoluteTime: boolean;
  legacyAxisDomain: [number, number] | null;
  orderedProgramWindows: TimelineProgramWindow[];
  assignProgramSeries: boolean;
  chartAxisEndMs: number;
  splitByProgram: boolean;
  highlightMode: boolean;
  windowColor: (window: TimelineProgramWindow, index: number) => string;
  windowHighlighted: (window: TimelineProgramWindow) => boolean;
};

const AXIS_END_EPS_SEC = 1e-6;

function mergeReleaseChartToFullMinuteSlots(
  points: ReleaseChartPoint[],
  axisEndSeconds: number,
  intervalSec: number,
): ReleaseChartPoint[] {
  const byBucket = new Map<number, ReleaseChartPoint>();
  for (const p of points) {
    const bucketStart = Math.floor(p.offsetSeconds / intervalSec) * intervalSec;
    if (bucketStart + intervalSec > axisEndSeconds + AXIS_END_EPS_SEC) {
      continue;
    }
    const prev = byBucket.get(bucketStart);
    if (!prev || p.viewers > prev.viewers) {
      byBucket.set(bucketStart, {
        offsetSeconds: bucketStart,
        viewers: Math.max(prev?.viewers ?? 0, p.viewers),
        likes: p.likes ?? prev?.likes,
        videoViewCount: p.videoViewCount ?? prev?.videoViewCount,
      });
      continue;
    }
    if (p.videoViewCount != null) {
      byBucket.set(bucketStart, { ...prev, videoViewCount: p.videoViewCount });
    }
  }
  return Array.from(byBucket.values()).sort((a, b) => a.offsetSeconds - b.offsetSeconds);
}

export function programSeriesKey(index: number): string {
  return `viewers_${index}`;
}

export function orderProgramWindowsByAppearance(
  windows: TimelineProgramWindow[],
): TimelineProgramWindow[] {
  return [...windows].sort(
    (a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime(),
  );
}

/** Union sibling + focus windows; keep a title when only one side has it. */
export function mergeTimelineProgramWindows(
  ...lists: TimelineProgramWindow[][]
): TimelineProgramWindow[] {
  const byKey = new Map<string, TimelineProgramWindow>();
  for (const list of lists) {
    for (const window of list) {
      const start = new Date(window.startedAt).getTime();
      if (!Number.isFinite(start)) continue;
      const key = `${String(window.id).trim()}|${start}`;
      const prev = byKey.get(key);
      if (!prev) {
        byKey.set(key, { ...window });
        continue;
      }
      if (!prev.title?.trim() && window.title?.trim()) prev.title = window.title;
      if (!prev.name?.trim() && window.name?.trim()) prev.name = window.name;
      if (prev.viewsAtStart == null && window.viewsAtStart != null) {
        prev.viewsAtStart = window.viewsAtStart;
      }
    }
  }
  return orderProgramWindowsByAppearance(Array.from(byKey.values()));
}

export function formatProgramWindowClockRange(
  window: TimelineProgramWindow,
  fallbackEndMs?: number,
): string {
  const start = formatExplorerTableTimeLine(window.startedAt);
  if (!start || start === "—") return "";
  const endIso = window.endedAt
    ? window.endedAt
    : fallbackEndMs != null && Number.isFinite(fallbackEndMs)
      ? new Date(fallbackEndMs).toISOString()
      : null;
  const end = endIso ? formatExplorerTableTimeLine(endIso) : "";
  if (!end || end === "—") return start;
  return `${start} – ${end}`;
}

function parseOptionalViewCount(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : null;
}

export function programViewsGained(
  videoViewCount?: number | null,
  viewsAtStart?: number | null,
): number | null {
  const now = parseOptionalViewCount(videoViewCount);
  const start = parseOptionalViewCount(viewsAtStart);
  if (now == null || start == null) return null;
  return Math.max(0, now - start);
}

export function windowStartVideoViews(
  window: TimelineProgramWindow,
  points: Array<{ axisTimeMs?: number; videoViewCount?: number }>,
): number | null {
  const stamped = parseOptionalViewCount(window.viewsAtStart);
  if (stamped != null) return stamped;
  const start = new Date(window.startedAt).getTime();
  if (!Number.isFinite(start)) return null;
  let atOrBefore: number | null = null;
  let firstIn: number | null = null;
  for (const point of points) {
    const views = parseOptionalViewCount(point.videoViewCount);
    const at = point.axisTimeMs;
    if (views == null || at == null || !Number.isFinite(at)) continue;
    if (at <= start) atOrBefore = views;
    else if (firstIn == null) firstIn = views;
  }
  return atOrBefore ?? firstIn;
}

export function programWindowLegendLabel(
  window: TimelineProgramWindow,
  fallbackName: string,
  fallbackEndMs?: number,
): string {
  const name = window.name.trim() || fallbackName;
  const range = formatProgramWindowClockRange(window, fallbackEndMs);
  return range ? `${name}  ${range}` : name;
}

/** Seconds from release to the last axis tick. */
export function viewerChartAxisEndSeconds(input: {
  highlightOnly?: boolean;
  releaseTime?: string;
  youtubeDuration?: number | null;
  totalDuration?: number | null;
  actualEndTime?: string;
  timelineDuration?: number | null;
}): number | null {
  const positive = (value?: number | null): number | null =>
    value != null && Number.isFinite(value) && value > 0 ? value : null;
  const youtube = positive(input.youtubeDuration);
  if (input.releaseTime && youtube != null) return youtube;
  const tracked = positive(input.totalDuration);
  if (tracked != null) return tracked;
  const releaseMs = input.releaseTime ? new Date(input.releaseTime).getTime() : NaN;
  if (input.releaseTime && input.actualEndTime && !Number.isNaN(releaseMs)) {
    const seconds = (new Date(input.actualEndTime).getTime() - releaseMs) / 1000;
    return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
  }
  return positive(input.timelineDuration);
}

export function windowForChartTime(
  ms: number,
  windows: TimelineProgramWindow[],
): { window: TimelineProgramWindow; index: number } | undefined {
  let match: { window: TimelineProgramWindow; index: number } | undefined;
  windows.forEach((window, index) => {
    const start = new Date(window.startedAt).getTime();
    if (!Number.isFinite(start) || ms < start) return;
    const end = window.endedAt ? new Date(window.endedAt).getTime() : NaN;
    if (Number.isFinite(end) && ms >= end) return;
    if (!match || start >= new Date(match.window.startedAt).getTime()) {
      match = { window, index };
    }
  });
  return match;
}

export const MUTED_VIEWER_CHART_COLOR = "#6b6b6b";

export function programWindowsOverlap(
  a: TimelineProgramWindow,
  b: TimelineProgramWindow,
): boolean {
  const a0 = new Date(a.startedAt).getTime();
  const b0 = new Date(b.startedAt).getTime();
  if (!Number.isFinite(a0) || !Number.isFinite(b0)) return false;
  const a1 = a.endedAt ? new Date(a.endedAt).getTime() : Number.POSITIVE_INFINITY;
  const b1 = b.endedAt ? new Date(b.endedAt).getTime() : Number.POSITIVE_INFINITY;
  return a0 < b1 && b0 < a1;
}

function normalizeHighlightName(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** Same catalog show, not a later title on the same live parent. */
export function programWindowsAreSameFocus(
  a: TimelineProgramWindow,
  b: TimelineProgramWindow,
): boolean {
  const aId = String(a.id || "").trim();
  const bId = String(b.id || "").trim();
  if (aId && bId && aId === bId) return true;
  const aName = normalizeHighlightName(a.name);
  const bName = normalizeHighlightName(b.name);
  if (!aName || !bName) return false;
  return aName === bName || aName.includes(bName) || bName.includes(aName);
}

export function programWindowIsHighlighted(
  window: TimelineProgramWindow,
  highlightWindows: TimelineProgramWindow[],
): boolean {
  if (highlightWindows.length === 0) return false;
  return highlightWindows.some((focus) =>
    programWindowsAreSameFocus(window, focus),
  );
}

export function programWindowChartColor(input: {
  accent: string;
  highlightOnly?: boolean;
  highlighted: boolean;
}): string {
  if (input.highlightOnly && !input.highlighted) return MUTED_VIEWER_CHART_COLOR;
  return input.accent;
}

export type LivestreamViewerTimelineChartProps = {
  livestreamId: string;
  releaseTime?: string;
  totalDuration?: number;
  youtubeDuration?: number;
  actualEndTime?: string;
  programWindows?: TimelineProgramWindow[];
  title?: string;
  /** Full muted curve; color only the focused program windows. */
  highlightOnly?: boolean;
  /** On a program page, the slice that stays in accent; siblings stay gray. */
  highlightWindows?: TimelineProgramWindow[];
};

export const LivestreamViewerTimelineChart: React.FC<
  LivestreamViewerTimelineChartProps
> = ({
  livestreamId,
  releaseTime,
  totalDuration,
  youtubeDuration,
  actualEndTime,
  programWindows = [],
  title,
  highlightOnly = false,
  highlightWindows = [],
}) => {
  const { t } = useTranslation("explorer");
  const chartCache = useRef<PreparedViewerChart | null>(null);
  const { data: timeline, isLoading, error } = useQuery<LivestreamTimelineResponse | null>({
    queryKey: ["livestream-timeline", livestreamId, VIEWER_CHART_INTERVAL_SEC],
    queryFn: () =>
      apiService.getLivestreamTimeline(livestreamId, VIEWER_CHART_INTERVAL_SEC, true),
    staleTime: LIVESTREAM_DETAIL_STALE_TIME,
    gcTime: LIVESTREAM_DETAIL_GC_TIME,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error || !timeline || !timeline.segments?.length) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        {t("noViewersData")}
      </Alert>
    );
  }

  const firstSegment = timeline.segments[0];
  const lastSegment = timeline.segments[timeline.segments.length - 1];
  const chartKey = [
    livestreamId,
    timeline.segments.length,
    firstSegment?.startSeconds ?? "",
    firstSegment?.viewers ?? "",
    lastSegment?.startSeconds ?? "",
    lastSegment?.viewers ?? "",
    releaseTime ?? "",
    actualEndTime ?? "",
    youtubeDuration ?? "",
    totalDuration ?? "",
    highlightOnly ? 1 : 0,
    programWindowsCacheKey(programWindows),
    programWindowsCacheKey(highlightWindows),
  ].join("|");

  if (chartCache.current?.key !== chartKey) {
  const releaseMs = releaseTime ? new Date(releaseTime).getTime() : NaN;
  const axisEndSeconds = viewerChartAxisEndSeconds({
    highlightOnly,
    releaseTime,
    youtubeDuration,
    totalDuration,
    actualEndTime,
    timelineDuration: timeline.duration,
  });
  const useReleaseWindow = Boolean(
    releaseTime &&
      !Number.isNaN(releaseMs) &&
      axisEndSeconds != null &&
      Number.isFinite(axisEndSeconds) &&
      axisEndSeconds > 0,
  );

  const actualStartMs = timeline.actualStartTime
    ? new Date(timeline.actualStartTime).getTime()
    : NaN;

  const chartDataReleaseRaw: ReleaseChartPoint[] =
    useReleaseWindow && axisEndSeconds != null
      ? timeline.segments
          .filter((s): s is typeof s & { viewers: number } => s.viewers != null)
          .map((s): ReleaseChartPoint | null => {
            let absoluteMs: number;
            if (s.absoluteStartTime) {
              absoluteMs = new Date(s.absoluteStartTime).getTime();
            } else if (!Number.isNaN(actualStartMs)) {
              absoluteMs = actualStartMs + s.startSeconds * 1000;
            } else {
              return null;
            }
            const offsetSeconds = (absoluteMs - releaseMs) / 1000;
            if (!Number.isFinite(offsetSeconds)) {
              return null;
            }
            return {
              offsetSeconds,
              viewers: s.viewers,
              likes: s.likes,
              videoViewCount: s.videoViewCount,
            };
          })
          .filter((d): d is ReleaseChartPoint => d !== null)
          .filter((d) => d.offsetSeconds >= 0 && d.offsetSeconds <= axisEndSeconds)
          .sort((a, b) => a.offsetSeconds - b.offsetSeconds)
      : [];

  const chartDataRelease: ReleaseChartPointWithAxis[] =
    chartDataReleaseRaw.length > 0 && axisEndSeconds != null
      ? mergeReleaseChartToFullMinuteSlots(
          chartDataReleaseRaw,
          axisEndSeconds,
          VIEWER_CHART_INTERVAL_SEC,
        ).map((p) => ({
          ...p,
          axisTimeMs: releaseMs + p.offsetSeconds * 1000,
        }))
      : [];

  const chartDataLegacy: LegacyChartPoint[] = timeline.segments
    .filter((s) => s.viewers != null)
    .map((s) => ({
      time: s.startTime,
      axisTimeMs: s.absoluteStartTime
        ? new Date(s.absoluteStartTime).getTime()
        : !Number.isNaN(actualStartMs)
          ? actualStartMs + s.startSeconds * 1000
          : undefined,
      viewers: s.viewers,
      likes: s.likes,
      videoViewCount: s.videoViewCount,
    }));

  const legacyHasAbsoluteTime = chartDataLegacy.some(
    (p) => p.axisTimeMs != null && Number.isFinite(p.axisTimeMs),
  );

  const effectiveUseReleaseWindow =
    useReleaseWindow && chartDataRelease.length > 0 && axisEndSeconds != null;
  const releaseChartDomainMax =
    effectiveUseReleaseWindow && axisEndSeconds != null
      ? Math.floor(axisEndSeconds / VIEWER_CHART_INTERVAL_SEC) * VIEWER_CHART_INTERVAL_SEC
      : null;
  const orderedProgramWindows = mergeTimelineProgramWindows(
    programWindows,
    highlightWindows,
  );
  const focusWindows = highlightWindows;
  const highlightMode = highlightOnly && orderedProgramWindows.length > 0;
  const splitByProgram = !highlightOnly && orderedProgramWindows.length > 1;
  const programAccents = programWindowChartAccents(orderedProgramWindows); // one color per program in this graph
  const windowHighlighted = (window: TimelineProgramWindow): boolean =>
    !highlightMode || programWindowIsHighlighted(window, focusWindows);
  const windowColor = (window: TimelineProgramWindow, index: number): string =>
    programWindowChartColor({
      accent: programAccents[index] ?? programWindowAccent(window, index),
      highlightOnly: highlightMode,
      highlighted: windowHighlighted(window),
    });
  const assignProgramSeries = splitByProgram || highlightMode;
  const chartData = (
    effectiveUseReleaseWindow ? chartDataRelease : chartDataLegacy
  ).map((point, index, points) => {
    if (!assignProgramSeries) return point;
    const ms =
      "axisTimeMs" in point && typeof point.axisTimeMs === "number"
        ? point.axisTimeMs
        : NaN;
    if (!Number.isFinite(ms) || point.viewers == null) return point;
    const assigned = windowForChartTime(ms, orderedProgramWindows);
    if (!assigned) {
      return point;
    }
    const next: Record<string, unknown> = {
      ...point,
      [programSeriesKey(assigned.index)]: point.viewers,
    };
    const prev = index > 0 ? points[index - 1] : undefined;
    const prevMs =
      prev && "axisTimeMs" in prev && typeof prev.axisTimeMs === "number"
        ? prev.axisTimeMs
        : NaN;
    const prevAssigned = Number.isFinite(prevMs)
      ? windowForChartTime(prevMs, orderedProgramWindows)
      : undefined;
    if (prevAssigned && prevAssigned.index !== assigned.index && prev?.viewers != null) {
      next[programSeriesKey(prevAssigned.index)] = point.viewers;
    }
    return next as ChartDataPoint;
  });

  const chartAxisEndMs = (() => {
    if (
      effectiveUseReleaseWindow &&
      releaseChartDomainMax != null &&
      !Number.isNaN(releaseMs)
    ) {
      return releaseMs + releaseChartDomainMax * 1000;
    }
    const times = chartData
      .map((point) =>
        "axisTimeMs" in point && typeof point.axisTimeMs === "number"
          ? point.axisTimeMs
          : NaN,
      )
      .filter((ms) => Number.isFinite(ms));
    return times.length > 0 ? Math.max(...times) : Date.now();
  })();

  const legacyAxisDomain =
    legacyHasAbsoluteTime && !effectiveUseReleaseWindow
      ? (() => {
          const times = chartDataLegacy
            .map((p) => p.axisTimeMs)
            .filter((ms): ms is number => ms != null && Number.isFinite(ms));
          if (times.length === 0) return null;
          return [Math.min(...times), Math.max(...times)] as [number, number];
        })()
      : null;

    chartCache.current = {
      key: chartKey,
      releaseMs,
      chartData,
      effectiveUseReleaseWindow,
      releaseChartDomainMax,
      legacyHasAbsoluteTime,
      legacyAxisDomain,
      orderedProgramWindows,
      assignProgramSeries,
      chartAxisEndMs,
      splitByProgram,
      highlightMode,
      windowColor,
      windowHighlighted,
    };
  }

  const prepared = chartCache.current;
  if (!prepared) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        {t("noViewersData")}
      </Alert>
    );
  }

  const {
    releaseMs,
    chartData,
    effectiveUseReleaseWindow,
    releaseChartDomainMax,
    legacyHasAbsoluteTime,
    legacyAxisDomain,
    orderedProgramWindows,
    assignProgramSeries,
    chartAxisEndMs,
    splitByProgram,
    highlightMode,
    windowColor,
    windowHighlighted,
  } = prepared;

  if (chartData.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        {t("noViewersData")}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1 }}>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
          {effectiveUseReleaseWindow &&
          releaseChartDomainMax != null &&
          !Number.isNaN(releaseMs) ? (
            <XAxis
              type="number"
              dataKey="axisTimeMs"
              domain={[releaseMs, releaseMs + releaseChartDomainMax * 1000]}
              tick={{ fontSize: 10 }}
              stroke="#666"
              tickFormatter={(v: number) =>
                formatLivestreamAxisTime(
                  Math.floor(v / (VIEWER_CHART_INTERVAL_SEC * 1000)) *
                    VIEWER_CHART_INTERVAL_SEC *
                    1000,
                )
              }
            />
          ) : legacyHasAbsoluteTime && legacyAxisDomain ? (
            <XAxis
              type="number"
              dataKey="axisTimeMs"
              domain={legacyAxisDomain}
              tick={{ fontSize: 10 }}
              stroke="#666"
              tickFormatter={(v: number) => formatLivestreamAxisTime(v)}
            />
          ) : (
            <XAxis
              dataKey="time"
              tick={{ fontSize: 10 }}
              stroke="#666"
              tickFormatter={(value: string) => value.substring(0, 5)}
            />
          )}
          <YAxis
            tick={{ fontSize: 10 }}
            stroke="#666"
            tickFormatter={(value) => formatNumber(value)}
          />
          <RechartsTooltip
            content={({ active, label, payload }) => {
              if (!active || !payload?.length) return null;
              const point = payload[0]?.payload as ChartDataPoint | undefined;
              const axisMs =
                point && "axisTimeMs" in point && typeof point.axisTimeMs === "number"
                  ? point.axisTimeMs
                  : Number(label);
              const timeLabel = Number.isFinite(axisMs)
                ? formatLivestreamChartDateTime(axisMs)
                : t("timeLabel", { label: label != null ? String(label) : "" });
              const viewers =
                point && "viewers" in point && point.viewers != null
                  ? point.viewers
                  : payload.find((item) => item.value != null)?.value;
              const videoViewCount =
                point && "videoViewCount" in point
                  ? parseOptionalViewCount(point.videoViewCount)
                  : null;
              const assigned =
                Number.isFinite(axisMs) && assignProgramSeries
                  ? windowForChartTime(axisMs, orderedProgramWindows)
                  : undefined;
              const programName =
                assigned?.window.name.trim() || t("program");
              const programRange = assigned
                ? formatProgramWindowClockRange(assigned.window, chartAxisEndMs)
                : "";
              const broadcastTitle =
                assigned?.window.title?.trim() ||
                title?.trim() ||
                timeline.title?.trim() ||
                "";
              const labelSx = {
                color: "text.secondary",
                display: "block",
                lineHeight: 1.45,
              } as const;
              return (
                <Box
                  sx={{
                    bgcolor: "#1a1a1a",
                    border: "1px solid #333",
                    borderRadius: 1,
                    px: 1.25,
                    py: 1,
                    maxWidth: 320,
                  }}
                >
                  <Typography variant="caption" sx={labelSx}>
                    {t("program")}:{" "}
                    <Box
                      component="span"
                      sx={{
                        color: assigned
                          ? windowColor(assigned.window, assigned.index)
                          : "white",
                        fontWeight: 600,
                      }}
                    >
                      {programName}
                    </Box>
                    {programRange ? `  ${programRange}` : `  ${timeLabel}`}
                  </Typography>
                  <Typography variant="caption" sx={{ ...labelSx, color: "white" }}>
                    {t("viewers")}:{" "}
                    {viewers != null ? formatNumber(Number(viewers)) : "—"}
                  </Typography>
                  <Typography variant="caption" sx={{ ...labelSx, color: "white" }}>
                    {t("viewsAtMoment")}:{" "}
                    {formatLivestreamTableViewsCount(videoViewCount)}
                  </Typography>
                  <Typography variant="caption" sx={{ ...labelSx, color: "white" }}>
                    {t("viewsGainedOnProgram")}:{" "}
                    {formatLivestreamTableViewsCount(
                      assigned
                        ? programViewsGained(
                            videoViewCount,
                            windowStartVideoViews(assigned.window, chartData),
                          )
                        : null,
                    )}
                  </Typography>
                  {broadcastTitle ? (
                    <Typography variant="caption" sx={labelSx}>
                      {t("broadcastTitle")}:{" "}
                      <Box component="span" sx={{ color: "white" }}>
                        {broadcastTitle}
                      </Box>
                    </Typography>
                  ) : null}
                </Box>
              );
            }}
          />
          {splitByProgram || highlightMode
            ? orderedProgramWindows.map((window, index) => {
                const start = new Date(window.startedAt).getTime();
                const end = window.endedAt
                  ? new Date(window.endedAt).getTime()
                  : chartAxisEndMs;
                if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
                  return null;
                }
                const highlighted = windowHighlighted(window);
                return (
                  <ReferenceArea
                    key={`band-${window.id}-${window.startedAt}-${index}`}
                    x1={start}
                    x2={end}
                    fill={windowColor(window, index)}
                    fillOpacity={
                      highlightMode ? (highlighted ? 0.16 : 0.05) : 0.08
                    }
                    ifOverflow="hidden"
                  />
                );
              })
            : null}
          {highlightMode ? (
            <Line
              type="monotone"
              dataKey="viewers"
              stroke={MUTED_VIEWER_CHART_COLOR}
              strokeWidth={2}
              dot={false}
              name={t("viewers")}
            />
          ) : null}
          {splitByProgram || highlightMode ? (
            orderedProgramWindows.map((window, index) => (
                <Line
                  key={`line-${window.id}-${window.startedAt}-${index}`}
                  type="monotone"
                  dataKey={programSeriesKey(index)}
                  stroke={windowColor(window, index)}
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                  name={programWindowLegendLabel(window, t("program"), chartAxisEndMs)}
                />
            ))
          ) : (
            <Line
              type="monotone"
              dataKey="viewers"
              stroke="#f44336"
              strokeWidth={2}
              dot={false}
              name="viewers"
            />
          )}
        </LineChart>
      </ResponsiveContainer>
      {splitByProgram || highlightMode ? (
        <Stack
          direction="row"
          flexWrap="wrap"
          useFlexGap
          spacing={1.5}
          justifyContent="center"
          sx={{ mt: 1.25, rowGap: 0.75, width: "100%" }}
        >
          {orderedProgramWindows.map((window, index) => {
            const color = windowColor(window, index);
            return (
              <Stack
                key={`legend-${window.id}-${window.startedAt}-${index}`}
                direction="row"
                alignItems="center"
                spacing={0.75}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: color,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="caption" sx={{ color }}>
                  {programWindowLegendLabel(window, t("program"), chartAxisEndMs)}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      ) : null}
    </Box>
  );
};
